import {
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useReactiveVar,
} from '@apollo/client';
import {
  getTheme,
  IColumn,
  IDetailsRowProps,
  IRenderFunction,
  PrimaryButton,
  SelectionMode,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { ActiveLink } from 'common/components/ActiveRowLink';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { FilterArrayType } from 'common/components/Filters';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import { TABLE_ROWS } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { EntityDeleteInput, UserProfileFilter } from 'common/types/globalTypes';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { getGlobalDateFormat } from 'common/utils/dateFormats';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  UserProfileDelete,
  UserProfileDeleteVariables,
} from './__generated__/UserProfileDelete';
import {
  UserProfiles,
  UserProfiles_userProfiles_nodes,
  UserProfilesVariables,
} from './__generated__/UserProfiles';
import { useStyles } from './index.styles';
import { UserSetupSearchBar } from './UserSetupSearchBar';
import { toOrderByVariable, toPaymentFilterVariable } from './utils';

import clsx from 'clsx';
import { RedBoxIndicator } from 'common/components/RedBoxIndicator';
import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import { EntityAction, EntityType } from 'common/types/utility';
import { getAcessStatusColor } from 'common/utils';
import {
  UserProfilesActiveCount,
  UserProfilesActiveCount_userProfiles_nodes,
  UserProfilesActiveCountVariables,
} from './__generated__/UserProfilesActiveCount';
import USERPROFILE_DELETE from './UserProfileDelete.graphql';
import USER_SETUP from './UserProfiles.graphql';
import USER_SETUP_ACTIVE_COUNT from './UserProfilesActiveCount.graphql';
import { useColumns } from './column.data';
import { setUserDefaults } from 'utility/cache/ui';

export type UserSetupListItem = UserProfiles_userProfiles_nodes & {
  roleName?: string;
  occupation?: string;
  departmentName?: string | null;
  accessPolicy?: string;
  approvalStatus?: string | null;
  inviteStatus?: string | null;
  rank?: string | null;
  securityLevel?: number | null;
  profileIconType?: string | null;
};
export type UserSetupListItemActive =
  UserProfilesActiveCount_userProfiles_nodes & {
    roleName?: string;
    occupation?: string;
    departmentName?: string | null;
    accessPolicy?: string;
    approvalStatus?: string | null;
    inviteStatus?: string | null;
    rank?: string | null;
    securityLevel?: number | null;
    profileIconType?: string | null;
  };

export const UserSetup: React.FC = () => {
  const commonStyles = useCommonStyles();
  const history = useHistory();
  const styles = useStyles();
  const userDefaults = useReactiveVar(setUserDefaults);
  const { columns } = useColumns(userDefaults);

  const renderRef = useRef(false);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [gridColumns, setGridColumns] = useState<ColumnData[]>(columns);
  const [searchFilters, setSearchFilters] = useState<FilterArrayType[]>();
  const [selectedRows, setSelectedRows] = useState<UserSetupListItemActive[]>(
    []
  );
  const [isActiveApprovalCount, setIsActiveApprovalCount] =
    useState<boolean>(false);
  const [fetchUserList, userListQueryInfo] = useLazyQuery<
    UserProfiles,
    UserProfilesVariables
  >(USER_SETUP, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['NAME_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
  });
  const [fetchUserListActive, userListActiveQueryInfo] = useLazyQuery<
    UserProfilesActiveCount,
    UserProfilesActiveCountVariables
  >(USER_SETUP_ACTIVE_COUNT, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['NAME_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
  });

  const [deleteUserProfile] = useMutation<
    UserProfileDelete,
    UserProfileDeleteVariables
  >(USERPROFILE_DELETE, {
    errorPolicy: 'all',
  });

  const fetch = isActiveApprovalCount ? fetchUserListActive : fetchUserList;
  const { loading, data, networkStatus, fetchMore, variables, error } =
    isActiveApprovalCount ? userListActiveQueryInfo : userListQueryInfo;
  const refetching = loading && networkStatus !== NetworkStatus.fetchMore;

  const transformedData: UserSetupListItemActive[] | undefined = refetching
    ? undefined
    : data?.userProfiles?.nodes.map((appr) => ({
        _activeApprovalCount: null,
        ...appr,
        roleName: appr.role?.name,
        occupation: appr.userOccupationTitle?.userOccupationTitle,
        departmentName: appr.department?.name,
        accessPolicy: appr.dataAccessPolicy?.name,
        inviteStatus: appr.inviteStatusType?.statusType,
        approvalStatus: appr.statusType?.statusType,
        rank: appr.userRankType?.userRank,
        securityLevel: appr.secureRowLevel?.securityLevel,
      }));

  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };
  const _onRenderRow: IRenderFunction<IDetailsRowProps> = (
    props,
    defaultRender
  ) => {
    if (!props) {
      return null;
    }
    const item: UserSetupListItem | UserSetupListItemActive = { ...props.item };
    const { _urgencyLevel } = { ...item };
    const newProps: IDetailsRowProps | undefined = props
      ? {
          ...props,
          className: clsx(
            styles.rowBaseStyle,
            _urgencyLevel === 0 ? commonStyles.urgentRow : styles.rowBaseStyle
          ),
        }
      : undefined;

    return <>{defaultRender?.(newProps)}</>;
  };
  const handleSearch = (showMore: boolean) => {
    const variables: UserProfilesVariables = {
      first: TABLE_ROWS,
      after: showMore ? data?.userProfiles?.pageInfo.endCursor : undefined,
      filter: searchFilters?.length
        ? ({
            and: toPaymentFilterVariable(searchFilters),
          } as UserProfileFilter)
        : undefined,
      orderBy: toOrderByVariable(sortOrderParam),
    };

    if (showMore) fetchMore?.({ variables });
    else fetch({ variables });
  };

  const handleSearchMemo = useCallback(handleSearch, [
    searchFilters,
    sortOrderParam,
    isActiveApprovalCount,
  ]);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false);
  }, [searchFilters, sortOrderParam, handleSearchMemo]);

  useEffect(() => {
    handleSearchMemo(false);
    renderRef.current = true;
  }, [handleSearchMemo]);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false);
  }, [handleSearchMemo]);

  const _renderItemColumn = (
    item: UserSetupListItemActive | undefined,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    if (item && column) {
      const key = column.key as keyof UserSetupListItemActive;
      const _isUrgentApproval = !Boolean(item._urgencyLevel);
      switch (key) {
        case '_urgencyLevel':
          return (
            <RedBoxIndicator
              _isUrgentApproval={_isUrgentApproval}
              itemId={item?.id}
            />
          );
        case 'name':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <ActiveLink to={`/account-management/users/user/${item?.id}`}>
                {item.name}
              </ActiveLink>
            </Stack>
          );
        case 'securityLevel':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignCenter}>
                {item.securityLevel}
              </Text>
            </Stack>
          );
        case '_activeApprovalCount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignCenter}>
                {item._activeApprovalCount}
              </Text>
            </Stack>
          );
        case 'invited':
        case '_lastUsedDate':
          const date = item[key];
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text>{date && getGlobalDateFormat(date)}</Text>
            </Stack>
          );
        case 'inviteStatus':
          return (
            <TooltipHost content={item.invited ?? ''}>
              <Stack
                verticalAlign="center"
                className={styles.onrenderColumnStack}
              >
                <Text>{item[key] ?? ''}</Text>
              </Stack>
            </TooltipHost>
          );
        case 'departmentOccupationTemplate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text>{item.departmentOccupationTemplate?.name ?? ''}</Text>
            </Stack>
          );
        case 'profileIconType':
          const color = getAcessStatusColor(item._signinInfo?.profileIconState);
          const theme = getTheme();

          const colorMap: Record<string, string> = {
            GREEN: theme.palette.green,
            RED: theme.palette.red,
            BLUE: theme.palette.themePrimary,
            YELLOW: theme.palette.yellow,
            MAGENTA: theme.palette.magenta,
          };

          const bgColor = colorMap[color] || theme.palette.themePrimary;

          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <TooltipHost content={item._signinInfo?.profileIconType ?? ''}>
                <Stack
                  className={styles.accessStatusContainer}
                  styles={{
                    root: {
                      backgroundColor: bgColor,
                    },
                  }}
                >
                  <Text variant="medium" className={styles.fontColor}>
                    {item._signinInfo?.profileIconType ?? ''}
                  </Text>
                </Stack>
              </TooltipHost>
            </Stack>
          );

        case 'isNewProjectIncluded':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text>{!!item[key] ? 'Yes' : 'No'}</Text>
            </Stack>
          );
        default:
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text>{item[key]?.toString() ?? ''}</Text>
            </Stack>
          );
      }
    }
  };
  const _onConfirm = async () => {
    const selectedData: UserSetupListItem[] | UserSetupListItemActive[] =
      selectedRows;
    const toDelete: UserSetupListItem[] | UserSetupListItemActive[] =
      selectedData.map((_, index) => {
        return selectedData![index];
      });
    let deletableUserProfiles = toDelete?.filter((item) => item._isDeletable);
    if (deletableUserProfiles.length > 0) {
      const deleteInputArray: EntityDeleteInput[] = deletableUserProfiles.map(
        (item) => {
          return { id: item.id, rowTimestamp: item._rowTimestamp! };
        }
      );
      const { errors } = await deleteUserProfile({
        variables: {
          input: {
            entityDelete: deleteInputArray,
          },
        },
        update: (cache, { data: mutationData }) => {
          const deletedIds =
            mutationData?.userProfileDelete?.deletedEntities?.map(
              (entity) => entity?.id
            );
          if (deletedIds) {
            const filteredList = data?.userProfiles?.nodes.filter(
              (user) => deletedIds.indexOf(user.id) === -1
            );
            const newData: UserProfiles = {
              userProfiles: {
                pageInfo: data?.userProfiles?.pageInfo!,
                totalCount: data?.userProfiles?.totalCount! - deletedIds.length,
                nodes: filteredList!,
              },
            };
            cache.writeQuery<UserProfiles, UserProfilesVariables>({
              query: USER_SETUP,
              variables,
              data: newData,
            });
          }
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else
        showCustomToast({
          content: 'User profiles deleted Successfully',
          appearance: 'success',
        });
    }
  };
  const onInviteProcess = async () => {
    // existing mutation is removed , new mutation to be added here
  };

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
            <Text variant="xLarge">User Setup</Text>
            <HelpIcon appLinkType="USER_SETUP" />
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <ActionMessageModal
              entityType={EntityType.User}
              disabled={!selectedRows.some((selected) => selected._isDeletable)}
              visible={selectedRows.length > 0}
              multiple={{
                validCount: selectedRows.filter(
                  (selected) => selected._isDeletable
                ).length,
                invalidNames: selectedRows
                  .filter((selected) => !selected._isDeletable)
                  .map((cannotDelete) => cannotDelete.name),
              }}
              onConfirm={_onConfirm}
            />
            <ActionMessageModal
              entityType={EntityType.User}
              action={EntityAction.Invite}
              disabled={
                !selectedRows.some((selected) => selected._isStagedToInvite)
              }
              visible={selectedRows.length > 0}
              multiple={{
                validCount: selectedRows.filter(
                  (selected) => selected._isStagedToInvite
                ).length,
                invalidNames: selectedRows
                  .filter((selected) => !selected._isStagedToInvite)
                  .map((cannotDelete) => cannotDelete.name),
              }}
              onConfirm={onInviteProcess}
              buttonProps={{ text: 'Invite Users' }}
            />
            <PrimaryButton
              onClick={() => history.push('/account-management/users/user')}
              iconProps={{
                iconName: 'Add',
              }}
              text="New User"
            />
          </Stack>
        </Stack>
        <Stack>
          <UserSetupSearchBar
            columns={gridColumns}
            onRefresh={() => handleSearch(false)}
            onFilterChange={(filters) => {
              setSearchFilters(filters);
            }}
            onToggleVisibility={(columns) => {
              setGridColumns(columns);
              const activeCountColumnAdded = columns.some(
                (item) =>
                  item.key === '_activeApprovalCount' && item.isVisible === true
              );
              setIsActiveApprovalCount(activeCountColumnAdded);
            }}
          />
        </Stack>
      </Stack>
      <InfiniteList<UserSetupListItemActive>
        loading={(!data && !error) || loading}
        onRenderItemColumn={_renderItemColumn}
        hasNextPage={data?.userProfiles?.pageInfo.hasNextPage}
        items={transformedData}
        selectionMode={SelectionMode.multiple}
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onSelectionChanged={setSelectedRows}
        onLoadMore={() => handleSearch(true)}
        onRenderRow={_onRenderRow}
      />
    </>
  );
};
